/**
 * Superadmin Feature Flags List Page
 */

import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import {
  FeatureFlagsTable,
  FeatureFlagsTableSkeleton,
  FeatureFlagsEmpty,
} from "../components/superadmin/FeatureFlagsTable";
import { CATEGORIES, type Category } from "../components/superadmin/feature-flags-constants";
import {
  useSuperadminFlags,
  useFlagOverrideCounts,
  useToggleFlagEnabled,
  useDeleteFlag,
  type FeatureFlag,
} from "../hooks/useSuperadminFeatureFlags";

export const Route = createFileRoute("/superadmin/feature-flags/")({
  component: FeatureFlagsListPage,
});

function FeatureFlagsListPage() {
  const [category, setCategory] = useState<Category>("all");
  const [flagToDelete, setFlagToDelete] = useState<FeatureFlag | null>(null);

  const { data, isLoading } = useSuperadminFlags();
  const { data: overrideCounts } = useFlagOverrideCounts(data?.flags);
  const toggleEnabled = useToggleFlagEnabled();
  const deleteFlag = useDeleteFlag();

  const flags = data?.flags ?? [];
  const filteredFlags = category === "all" ? flags : flags.filter((f) => f.category === category);

  const Header = (
    <div className="flex items-center justify-between">
      <Tabs value={category} onValueChange={(v) => setCategory(v as Category)}>
        <TabsList>
          {CATEGORIES.map((c) => (
            <TabsTrigger key={c.value} value={c.value}>{c.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <Button asChild>
        <Link to="/superadmin/feature-flags/new">
          <FontAwesomeIcon icon={faPlus} className="h-4 w-4 mr-2" />New Flag
        </Link>
      </Button>
    </div>
  );

  if (isLoading) {
    return <div className="max-w-5xl mx-auto space-y-4">{Header}<FeatureFlagsTableSkeleton /></div>;
  }

  if (flags.length === 0) {
    return <div className="max-w-5xl mx-auto space-y-4">{Header}<FeatureFlagsEmpty /></div>;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      {Header}

      <FeatureFlagsTable
        flags={filteredFlags}
        overrideCounts={overrideCounts}
        isToggling={toggleEnabled.isPending}
        onToggle={(id, enabled) => toggleEnabled.mutate({ id, enabled })}
        onDelete={setFlagToDelete}
      />

      <AlertDialog open={!!flagToDelete} onOpenChange={(open) => !open && setFlagToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete feature flag?</AlertDialogTitle>
            <AlertDialogDescription>
              This will soft-delete <strong>{flagToDelete?.key}</strong>. It can be restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => flagToDelete && deleteFlag.mutate(flagToDelete.id, { onSuccess: () => setFlagToDelete(null) })}
              disabled={deleteFlag.isPending}
              className="bg-muted text-destructive hover:bg-muted/80"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
